/**
 * E-GTCC-VIEW-DPLANE1 — tab "Control Plane (Inventar)".
 *
 * The four inventory tables of the old DB-Ops console (Hosts, Instanzen,
 * Datenbanken, Zuordnungen), rebuilt in the reference building blocks
 * (Panel / Table / Dot). Every column shows a value the read-only endpoints
 * `/v1/admin/data-plane/db-ops/{hosts,postgres-instances,databases,
 * tenant-assignments}` already report; nothing is derived and nothing is
 * written.
 */
import type { ReactNode } from "react";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";

import type {
  DbOpsDatabase,
  DbOpsHost,
  PostgresInstance,
  TenantDatabaseAssignment,
} from "../../../../db-ops/types.js";
import {
  fetchDplaneAssignments,
  fetchDplaneDatabases,
  fetchDplaneHosts,
  fetchDplaneInstances,
  DPLANE_ASSIGNMENTS_KEY,
  DPLANE_DATABASES_KEY,
  DPLANE_HOSTS_KEY,
  DPLANE_INSTANCES_KEY,
} from "../../../api/dplane.js";
import {
  FleetDash,
  FleetDot,
  GTCC_VISUALLY_HIDDEN,
  NotMeasurable,
  type FleetTone,
} from "../../cockpit/components/FleetPrimitives.js";
import {
  asList,
  DplaneGate,
  DplanePanel,
  DPLANE_QUERY_OPTIONS,
  GRID_COLUMN_STYLE,
  HOST_STYLE,
  MONO_STYLE,
  TABLE_CELL_STYLE,
  TABLE_HEAD_STYLE,
  TABLE_STYLE,
  TABLE_WRAP_STYLE,
  type DplaneTabProps,
} from "../components/DplanePanels.js";
import {
  toneOfAssignment,
  toneOfDatabase,
  toneOfHostLifecycle,
  toneOfInstance,
} from "../components/dplaneTone.js";

/** A status cell: the reference's `.dot` plus the status word. */
function StatusCell(props: { tone: FleetTone; label: string; testId: string }): ReactNode {
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: "var(--gt-sys-space-2)" }}
      data-testid={props.testId}
      data-tone={props.tone}
    >
      <FleetDot tone={props.tone} />
      {props.label}
    </span>
  );
}

function InventoryTable(props: {
  columns: ReadonlyArray<string>;
  caption: string;
  testId: string;
  rows: ReadonlyArray<ReactNode>;
}): ReactNode {
  if (props.rows.length === 0) {
    return <NotMeasurable testId={`${props.testId}-empty`} />;
  }
  return (
    <div style={TABLE_WRAP_STYLE}>
      <table style={TABLE_STYLE} data-testid={props.testId}>
        {/*
         * Die Tabellen der Referenz tragen keine sichtbare Bildunterschrift
         * (`GTCC_REFERENCE_2026-09-01.html:924-929`), und diese vier nennen
         * ohnehin nur die Spalten noch einmal. Die Tabellen-Inventur des
         * Repos verlangt sie trotzdem, also bleibt sie im Baum und verlaesst
         * nur die Flaeche (`FleetPrimitives.tsx:438`). Die Bildunterschriften
         * mit Zahl (Monitoring, Plan-Historie) bleiben sichtbar: sie nennen
         * einen gemessenen Wert, der sonst nirgends steht.
         */}
        <caption style={GTCC_VISUALLY_HIDDEN}>{props.caption}</caption>
        <thead>
          <tr>
            {props.columns.map((column) => (
              <th key={column} scope="col" style={TABLE_HEAD_STYLE}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{props.rows}</tbody>
      </table>
    </div>
  );
}

function optional(value: string | undefined): ReactNode {
  return value === undefined || value === "" ? <FleetDash /> : value;
}

function HostsPanel(): ReactNode {
  const { t } = useLocale();
  const query = useQuery({
    queryKey: DPLANE_HOSTS_KEY,
    queryFn: ({ signal }) => fetchDplaneHosts(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  return (
    <DplanePanel title={t("admin.gtcc.dplane.cp.hosts.title")} testId="gtcc-dplane-cp-hosts">
      <DplaneGate
        query={query}
        testId="gtcc-dplane-cp-hosts-panel"
        render={(hosts: ReadonlyArray<DbOpsHost>) => (
          <InventoryTable
            testId="gtcc-dplane-cp-hosts-table"
            caption={t("admin.gtcc.dplane.cp.hosts.caption")}
            columns={[
              t("admin.gtcc.dplane.cp.hosts.col.host"),
              t("admin.gtcc.dplane.cp.hosts.col.role"),
              t("admin.gtcc.dplane.cp.hosts.col.provider"),
              t("admin.gtcc.dplane.cp.hosts.col.region"),
              t("admin.gtcc.dplane.cp.hosts.col.status"),
            ]}
            rows={asList(hosts).map((host) => (
              <tr key={host.id} data-testid={`gtcc-dplane-cp-host-${host.id}`}>
                {/* `td` wie in der Referenz, kein Zeilenkopf. */}
                <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>{host.hostname}</td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                  {t(`admin.gtcc.dplane.host_role.${host.role}`)}
                </td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                  {optional(host.providerLabel)}
                </td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{optional(host.regionLabel)}</td>
                <td style={TABLE_CELL_STYLE}>
                  <StatusCell
                    tone={toneOfHostLifecycle(host.lifecycleStatus)}
                    label={t(`admin.gtcc.dplane.host_lifecycle.${host.lifecycleStatus}`)}
                    testId={`gtcc-dplane-cp-host-status-${host.id}`}
                  />
                </td>
              </tr>
            ))}
          />
        )}
      />
    </DplanePanel>
  );
}

function InstancesPanel(): ReactNode {
  const { t } = useLocale();
  const query = useQuery({
    queryKey: DPLANE_INSTANCES_KEY,
    queryFn: ({ signal }) => fetchDplaneInstances(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  return (
    <DplanePanel
      title={t("admin.gtcc.dplane.cp.instances.title")}
      testId="gtcc-dplane-cp-instances"
    >
      <DplaneGate
        query={query}
        testId="gtcc-dplane-cp-instances-panel"
        render={(instances: ReadonlyArray<PostgresInstance>) => (
          <InventoryTable
            testId="gtcc-dplane-cp-instances-table"
            caption={t("admin.gtcc.dplane.cp.instances.caption")}
            columns={[
              t("admin.gtcc.dplane.cp.instances.col.instance"),
              t("admin.gtcc.dplane.cp.instances.col.host"),
              t("admin.gtcc.dplane.cp.instances.col.role"),
              t("admin.gtcc.dplane.cp.instances.col.version"),
              t("admin.gtcc.dplane.cp.instances.col.bind"),
              t("admin.gtcc.dplane.cp.instances.col.status"),
            ]}
            rows={asList(instances).map((instance) => (
              <tr key={instance.id} data-testid={`gtcc-dplane-cp-instance-${instance.id}`}>
                <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>{instance.id}</td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{instance.hostId}</td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                  {t(`admin.gtcc.dplane.instance_role.${instance.role}`)}
                </td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{instance.postgresVersion}</td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{instance.bindScopeLabel}</td>
                <td style={TABLE_CELL_STYLE}>
                  <StatusCell
                    tone={toneOfInstance(instance.status)}
                    label={t(`admin.gtcc.dplane.instance_status.${instance.status}`)}
                    testId={`gtcc-dplane-cp-instance-status-${instance.id}`}
                  />
                </td>
              </tr>
            ))}
          />
        )}
      />
    </DplanePanel>
  );
}

function DatabasesPanel(): ReactNode {
  const { t } = useLocale();
  const query = useQuery({
    queryKey: DPLANE_DATABASES_KEY,
    queryFn: ({ signal }) => fetchDplaneDatabases(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  return (
    <DplanePanel
      title={t("admin.gtcc.dplane.cp.databases.title")}
      testId="gtcc-dplane-cp-databases"
    >
      <DplaneGate
        query={query}
        testId="gtcc-dplane-cp-databases-panel"
        render={(databases: ReadonlyArray<DbOpsDatabase>) => (
          <InventoryTable
            testId="gtcc-dplane-cp-databases-table"
            caption={t("admin.gtcc.dplane.cp.databases.caption")}
            columns={[
              t("admin.gtcc.dplane.cp.databases.col.database"),
              t("admin.gtcc.dplane.cp.databases.col.instance"),
              t("admin.gtcc.dplane.cp.databases.col.purpose"),
              t("admin.gtcc.dplane.cp.databases.col.tenant"),
              t("admin.gtcc.dplane.cp.databases.col.status"),
            ]}
            rows={asList(databases).map((database) => (
              <tr key={database.id} data-testid={`gtcc-dplane-cp-database-${database.id}`}>
                <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>{database.name}</td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{database.instanceId}</td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                  {t(`admin.gtcc.dplane.database_purpose.${database.purpose}`)}
                </td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                  {optional(database.tenantId)}
                </td>
                <td style={TABLE_CELL_STYLE}>
                  <StatusCell
                    tone={toneOfDatabase(database.status)}
                    label={t(`admin.gtcc.dplane.database_status.${database.status}`)}
                    testId={`gtcc-dplane-cp-database-status-${database.id}`}
                  />
                </td>
              </tr>
            ))}
          />
        )}
      />
    </DplanePanel>
  );
}

function AssignmentsPanel(): ReactNode {
  const { t } = useLocale();
  const query = useQuery({
    queryKey: DPLANE_ASSIGNMENTS_KEY,
    queryFn: ({ signal }) => fetchDplaneAssignments(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  return (
    <DplanePanel
      title={t("admin.gtcc.dplane.cp.assignments.title")}
      testId="gtcc-dplane-cp-assignments"
    >
      <DplaneGate
        query={query}
        testId="gtcc-dplane-cp-assignments-panel"
        render={(assignments: ReadonlyArray<TenantDatabaseAssignment>) => (
          <InventoryTable
            testId="gtcc-dplane-cp-assignments-table"
            caption={t("admin.gtcc.dplane.cp.assignments.caption")}
            columns={[
              t("admin.gtcc.dplane.cp.assignments.col.tenant"),
              t("admin.gtcc.dplane.cp.assignments.col.database"),
              t("admin.gtcc.dplane.cp.assignments.col.pool"),
              t("admin.gtcc.dplane.cp.assignments.col.status"),
            ]}
            rows={asList(assignments).map((assignment) => (
              <tr key={assignment.id} data-testid={`gtcc-dplane-cp-assignment-${assignment.id}`}>
                <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>
                  {assignment.tenantLabel ?? assignment.tenantId}
                </td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{assignment.databaseId}</td>
                <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                  {optional(assignment.poolId)}
                </td>
                <td style={TABLE_CELL_STYLE}>
                  <StatusCell
                    tone={toneOfAssignment(assignment.status)}
                    label={t(`admin.gtcc.dplane.assignment_status.${assignment.status}`)}
                    testId={`gtcc-dplane-cp-assignment-status-${assignment.id}`}
                  />
                </td>
              </tr>
            ))}
          />
        )}
      />
    </DplanePanel>
  );
}

export function InventoryTab(_props: DplaneTabProps): ReactNode {
  return (
    <div style={GRID_COLUMN_STYLE} data-testid="gtcc-dplane-cp-grid">
      <HostsPanel />
      <InstancesPanel />
      <DatabasesPanel />
      <AssignmentsPanel />
    </div>
  );
}
